import React, { Component } from "react";
import { message, Row, Col, Divider, Alert, Popconfirm } from "antd";
import axios from "axios";
import { connect } from "react-redux";
import {
  Form,
  FormItem,
  InputNumberItem,
  TextAreaItem,
  ButtonItem,
  ModalItem,
  dineroDisabledMask,
  round2Decimals,
} from "./Items";
import SelectProveedoresItem from "./SelectProveedoresItem";

/**
 * Captura del ajuste manual de un movimiento de la DIOT.
 *
 * El ajuste SUSTITUYE lo que el motor calculo para ese movimiento; no se suma. Por eso
 * el formulario arranca precargado con los importes calculados: corregir es editar un
 * numero, no capturar los doce desde cero, y asi lo que no se toca queda igual.
 *
 * Se usa desde dos lados: el papel de trabajo del reporte DIOT (que ya trae los
 * calculados en el renglon) y Ver Poliza (que no los tiene y los pide al backend).
 */

// Los 12 campos que el motor puebla, agrupados como los lee un contador.
const GRUPOS_CAMPOS = [
  {
    titulo: "Tasa 16%",
    campos: [
      { name: "monto12", label: "Base 16%" },
      { name: "ivaCampo22", label: "IVA 16% Acreditable" },
      { name: "ivaNCampo37", label: "IVA 16% No Acreditable" },
      { name: "monto13", label: "Devoluciones 16%" },
    ],
  },
  {
    titulo: "Tasa 8%",
    campos: [
      { name: "monto8", label: "Base 8%" },
      { name: "ivaCampo18", label: "IVA 8% Acreditable" },
      { name: "ivaNCampo29", label: "IVA 8% No Acreditable" },
      { name: "monto9", label: "Devoluciones 8%" },
    ],
  },
  {
    titulo: "Otros actos",
    campos: [
      { name: "monto51", label: "Base 0%" },
      { name: "monto50", label: "Exentos" },
      { name: "monto52", label: "No Objeto" },
      { name: "ivaRetenido48", label: "IVA Retenido" },
    ],
  },
];

const NOMBRES_CAMPOS = GRUPOS_CAMPOS.flatMap((grupo) => grupo.campos.map((campo) => campo.name));

// Mismas 3 columnas de control que calcula el backend por renglon.
const totalesControl = (valores) => {
  const n = (campo) => Number(valores?.[campo]) || 0;
  return {
    // monto9 y monto13 son devoluciones: el valor de actos es neto de ellas.
    valorActos: round2Decimals(n("monto8") - n("monto9") + n("monto12") - n("monto13")),
    ivaAcreditable: round2Decimals(n("ivaCampo18") + n("ivaCampo22")),
    ivaNoAcreditable: round2Decimals(n("ivaNCampo29") + n("ivaNCampo37")),
  };
};

class ModalAjusteDiot extends Component {
  formRef = React.createRef();

  state = {
    loading: false,
    calculado: null,
    ajuste: null,
    descripcionMovimiento: "",
    errorCalculo: null,
    // Espejo de los valores del formulario, solo para pintar el comparativo en vivo.
    valores: {},
  };

  componentDidUpdate(prevProps) {
    if (this.props.visible && !prevProps.visible) this.cargar();
  }

  cargar() {
    // Desde el papel de trabajo los calculados ya viajan en el renglon: no tiene caso
    // volver a correr el motor para un solo movimiento.
    if (this.props.calculado) {
      this.aplicarDatos({
        calculado: this.props.calculado,
        ajuste: this.props.ajuste || null,
        descripcionMovimiento: this.props.descripcionMovimiento || "",
        errorCalculo: this.props.errorCalculo || null,
      });
      return;
    }

    this.setState({ loading: true });
    axios
      .post(
        "ajustesDiot/datosAjusteDiot",
        { ...this.props.ancla, empresaId: this.props.empresaId },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        this.setState({ loading: false });
        if (res.data.success === 2) {
          this.aplicarDatos(res.data);
        } else {
          message.error(res.data.message || "No se pudo cargar el movimiento");
          this.props.onCancel();
        }
      })
      .catch(() => {
        this.setState({ loading: false });
        message.error("No se pudo cargar el movimiento");
      });
  }

  aplicarDatos({ calculado, ajuste, descripcionMovimiento, errorCalculo, proveedorId }) {
    // Si ya hay ajuste guardado gana el ajuste; si no, se precargan los calculados.
    const base = ajuste || calculado || {};
    const valores = {};
    for (const nombre of NOMBRES_CAMPOS) valores[nombre] = round2Decimals(Number(base[nombre]) || 0);

    this.setState(
      {
        calculado: calculado || {},
        ajuste: ajuste || null,
        descripcionMovimiento: descripcionMovimiento || "",
        errorCalculo: errorCalculo || null,
        valores,
      },
      () => {
        this.formRef.current?.setFieldsValue({
          ...valores,
          proveedorId: ajuste?.proveedorId || undefined,
          motivo: ajuste?.motivo || "",
        });
      }
    );
    // El proveedorId del movimiento solo sirve de referencia visual; el campo del
    // formulario queda vacio cuando no hay reasignacion, que significa "deja el suyo".
    this.proveedorOriginalId = proveedorId;
  }

  guardar(values) {
    this.setState({ loading: true });

    const cuerpo = {
      ...this.props.ancla,
      empresaId: this.props.empresaId,
      proveedorId: values.proveedorId || null,
      motivo: values.motivo || "",
      // Se manda la foto de lo calculado para dejarla guardada como evidencia de sobre
      // que numeros se capturo el ajuste.
      calculado: this.state.calculado,
    };
    for (const nombre of NOMBRES_CAMPOS) cuerpo[nombre] = Number(values[nombre]) || 0;

    axios
      .post("ajustesDiot/guardarAjusteDiot", cuerpo, { headers: this.props.headersToken })
      .then((res) => {
        this.setState({ loading: false });
        if (res.data.success === 2) {
          message.success("Ajuste guardado. La DIOT usara estos importes en lugar de los calculados");
          this.props.onGuardado();
        } else {
          message.error(res.data.message || "Ha ocurrido un error al guardar el ajuste");
        }
      })
      .catch(() => {
        this.setState({ loading: false });
        message.error("Ha ocurrido un error al guardar el ajuste");
      });
  }

  eliminar() {
    if (!this.state.ajuste) return;
    this.setState({ loading: true });
    axios
      .post(
        "ajustesDiot/eliminarAjusteDiot",
        { ajusteId: this.state.ajuste._id, empresaId: this.props.empresaId },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        this.setState({ loading: false });
        if (res.data.success === 2) {
          message.success("Ajuste eliminado. El movimiento vuelve al calculo del sistema");
          this.props.onGuardado();
        } else {
          message.error(res.data.message || "Ha ocurrido un error al eliminar el ajuste");
        }
      })
      .catch(() => {
        this.setState({ loading: false });
        message.error("Ha ocurrido un error al eliminar el ajuste");
      });
  }

  renderComparativo() {
    const calculado = totalesControl(this.state.calculado);
    const ajustado = totalesControl(this.state.valores);
    const filas = [
      { label: "Valor de actos", clave: "valorActos" },
      { label: "IVA acreditable", clave: "ivaAcreditable" },
      { label: "IVA no acreditable", clave: "ivaNoAcreditable" },
    ];

    return (
      <div style={{ background: "#FAFAFA", padding: "8px 12px", borderRadius: 4, marginBottom: 10 }}>
        <Row gutter={8} style={{ fontWeight: 600, fontSize: 12, color: "#8c8c8c" }}>
          <Col span={9}>Totales de control</Col>
          <Col span={5} style={{ textAlign: "right" }}>Calculado</Col>
          <Col span={5} style={{ textAlign: "right" }}>Ajustado</Col>
          <Col span={5} style={{ textAlign: "right" }}>Diferencia</Col>
        </Row>
        {filas.map((fila) => {
          const diferencia = round2Decimals(ajustado[fila.clave] - calculado[fila.clave]);
          return (
            <Row gutter={8} key={fila.clave} style={{ fontSize: 12 }}>
              <Col span={9}>{fila.label}</Col>
              <Col span={5} style={{ textAlign: "right", color: "#8c8c8c" }}>
                {dineroDisabledMask(calculado[fila.clave])}
              </Col>
              <Col span={5} style={{ textAlign: "right", fontWeight: 600 }}>
                {dineroDisabledMask(ajustado[fila.clave])}
              </Col>
              <Col
                span={5}
                style={{ textAlign: "right", color: diferencia === 0 ? "#8c8c8c" : "#fa8c16" }}
              >
                {dineroDisabledMask(diferencia)}
              </Col>
            </Row>
          );
        })}
      </div>
    );
  }

  render() {
    const calculado = this.state.calculado || {};

    return (
      <ModalItem
        title="Ajustar lo que este movimiento aporta a la DIOT"
        spinning={this.state.loading}
        visible={this.props.visible}
        onCancel={this.props.onCancel}
        width={720}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 10 }}
          message="Los importes que captures SUSTITUYEN a los calculados"
          description={
            "El movimiento seguira apareciendo una sola vez en el reporte, marcado como ajustado. " +
            "No se suma nada encima, asi que no hay riesgo de duplicar."
          }
        />

        {this.state.descripcionMovimiento ? (
          <div style={{ marginBottom: 10 }}>
            <b>Movimiento:</b> {this.state.descripcionMovimiento}
          </div>
        ) : null}

        {this.state.errorCalculo ? (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 10 }}
            message="El sistema no pudo calcular este movimiento"
            description={`${this.state.errorCalculo}. Los importes llegan en cero; captura a mano los que correspondan.`}
          />
        ) : null}

        {this.renderComparativo()}

        <Form
          ref={this.formRef}
          onFinish={this.guardar.bind(this)}
          onValuesChange={(_, todos) => this.setState({ valores: todos })}
        >
          {GRUPOS_CAMPOS.map((grupo) => (
            <div key={grupo.titulo}>
              <Divider orientation="left" style={{ margin: "8px 0", fontSize: 13 }}>
                {grupo.titulo}
              </Divider>
              {grupo.campos.map((campo) => (
                <Row gutter={8} align="middle" key={campo.name} style={{ marginBottom: 2 }}>
                  <Col span={10} style={{ fontSize: 13 }}>{campo.label}</Col>
                  <Col span={6} style={{ textAlign: "right", color: "#8c8c8c", fontSize: 13 }}>
                    {dineroDisabledMask(Number(calculado[campo.name]) || 0)}
                  </Col>
                  <Col span={8}>
                    {/* Sin la prop `number`: su regla exige "mayor a cero" y aqui el cero
                        es un valor legitimo, ademas de que el IVA retenido de una nota de
                        credito viene en negativo. */}
                    <FormItem name={campo.name} noStyle>
                      <InputNumberItem width="100%" dinero />
                    </FormItem>
                  </Col>
                </Row>
              ))}
            </div>
          ))}

          <Divider orientation="left" style={{ margin: "8px 0", fontSize: 13 }}>
            Tercero y justificacion
          </Divider>

          <FormItem label="Reasignar proveedor" name="proveedorId" margin>
            <SelectProveedoresItem
              empresaId={this.props.empresaId}
              headersToken={this.props.headersToken}
              allowClear
              width="100%"
              placeholder="Dejar vacio para conservar el del movimiento"
            />
          </FormItem>

          <FormItem label="Motivo" name="motivo" required margin>
            <TextAreaItem rows={2} placeholder="Por que se corrige este movimiento" />
          </FormItem>

          <ButtonItem
            type="primary"
            htmlType="submit"
            block
            style={{ marginTop: 10 }}
            loading={this.state.loading}
          >
            Guardar ajuste
          </ButtonItem>

          {this.state.ajuste ? (
            <Popconfirm
              title="El movimiento volvera a usar el calculo del sistema. Continuar?"
              okText="Si, eliminar"
              cancelText="Cancelar"
              onConfirm={this.eliminar.bind(this)}
            >
              <ButtonItem blanco block style={{ marginTop: 8 }} loading={this.state.loading}>
                Restaurar calculo del sistema
              </ButtonItem>
            </Popconfirm>
          ) : null}
        </Form>
      </ModalItem>
    );
  }
}

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
});

export default connect(mapStateToProps)(ModalAjusteDiot);
